import React from 'react';
import { useTranslation } from 'react-i18next';
import { groupApplicationsByRestaurant, nonZeroStatuses } from '../utils/orgRecipes';

/**
 * Resumen por local de una receta estándar (RES-45): qué pasó (o pasaría, en la vista previa)
 * con el producto de cada sucursal y qué locales no tienen ese producto en la carta.
 *
 * @param {Object} props
 * @param {Array} props.applications - `{ restaurantId, restaurantName, itemId, itemName, status, recipeVersion }`
 * @param {Object} props.summary - `{ byStatus, following, restaurantsWithoutProduct }`
 */
const OrgRecipeApplicationSummary = ({ applications = [], summary = null }) => {
  const { t } = useTranslation();
  const groups = groupApplicationsByRestaurant(applications);
  const statuses = nonZeroStatuses(summary);
  const withoutProduct = summary?.restaurantsWithoutProduct || [];

  return (
    <div className="org-recipe-summary">
      {statuses.length > 0 && (
        <ul
          className="org-recipe-summary-chips"
          aria-label={t('inventory.orgRecipes.summary.byStatus')}
        >
          {statuses.map(({ status, count }) => (
            <li
              key={status}
              className={`org-recipe-status org-recipe-status--${status}`}
              data-status={status}
            >
              {t(`inventory.orgRecipes.status.${status}`)}: <strong>{count}</strong>
            </li>
          ))}
        </ul>
      )}

      {groups.length > 0 ? (
        <table className="org-recipe-summary-table">
          <thead>
            <tr>
              <th>{t('inventory.orgRecipes.summary.restaurant')}</th>
              <th>{t('inventory.orgRecipes.summary.product')}</th>
              <th>{t('inventory.orgRecipes.summary.status')}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) =>
              group.items.map((a, idx) => (
                <tr key={`${a.restaurantId}-${a.itemId}`} data-restaurant={a.restaurantId}>
                  <td>{idx === 0 ? group.restaurantName : ''}</td>
                  <td>{a.itemName}</td>
                  <td>
                    <span
                      className={`org-recipe-status org-recipe-status--${a.status}`}
                      data-status={a.status}
                    >
                      {t(`inventory.orgRecipes.status.${a.status}`)}
                      {a.recipeVersion != null &&
                        (a.status === 'applied' || a.status === 'up_to_date') &&
                        ` · v${a.recipeVersion}`}
                    </span>
                    <span className="org-recipe-status-hint">
                      {t(`inventory.orgRecipes.statusHint.${a.status}`)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : (
        <p className="org-recipe-muted">{t('inventory.orgRecipes.summary.noProducts')}</p>
      )}

      {withoutProduct.length > 0 && (
        <div className="org-recipe-without-product">
          <h4>{t('inventory.orgRecipes.summary.withoutProduct')}</h4>
          <ul>
            {withoutProduct.map((r) => (
              <li key={r.restaurantId} data-restaurant={r.restaurantId}>
                {r.name || r.restaurantId}
              </li>
            ))}
          </ul>
          <p className="org-recipe-muted">{t('inventory.orgRecipes.summary.withoutProductHint')}</p>
        </div>
      )}
    </div>
  );
};

export default OrgRecipeApplicationSummary;
